import {
  SurveysPermissions,
  SurveyAnswersPermissions,
} from "@graffer-inc/smart-apply";
import { GroupRole } from "../group/group";

export type Roles = "Staff" | "Admin" | "GrafferStaff" | "AccountManager";

export const ApplicationPaymentPermissions = [
  "applications.post_payment_add",
  "applications.post_payment_withdrawn",
  "applications.payment_refund",
  "applications.payment_adjustment",
] as const;

const ApplicationsPermissions = [
  // Applications
  "applications.list",
  "applications.zipped_attach_file_download",
  "applications.read",
  // "applications.create",
  "applications.update",
  "applications.output_pdf",
  "applications.count",
  ...ApplicationPaymentPermissions,
] as const;

export const ApplicationRemovalTasksPermissions = [
  "applications.removal_tasks_read",
  "applications.removal_tasks_create",
  "applications.removal_tasks_cancel",
] as const;

const ProductDataRemovalTasksPermissions = [
  "product_data_removal_tasks.applications.read",
  "product_data_removal_tasks.applications.create",
  "product_data_removal_tasks.applications.approve",
  "product_data_removal_tasks.applications.cancel",
  "product_data_removal_tasks.survey_answers.read",
  "product_data_removal_tasks.survey_answers.create",
  "product_data_removal_tasks.survey_answers.approve",
  "product_data_removal_tasks.survey_answers.cancel",
] as const;

const UsersPermissions = [
  "users.create",
  "users.read",
  "users.update",
  "users.password_reset",
  "users.login_lock_reset",
  "users.disable",
  "users.enable",
  "users.disable_totp",
  "users.manage_groups",
  "users.auth_method_update",
] as const;

const ServiceAccountsPermissions = [
  "service_account.service_account.create",
  "service_account.service_account.read",
  "service_account.service_account.update",
  "service_account.service_account.disable",
  "service_account.service_account_metadata.read",
  "service_account.service_account_key.create",
  "service_account.service_account_key.read",
  "service_account.service_account_key.disable",
] as const;

const OrgSamlSettingPermissions = [
  "org_saml_settings.create",
  "org_saml_settings.update",
  "org_saml_settings.read",
] as const;

const OrgSessionSettingPermissions = [
  "tenant_management.org_session_settings.read",
  "tenant_management.org_session_settings.update",
] as const;

const QualifiedInvoiceBusinessIssuersPermissions = [
  "invoice_business_issuers.read",
  "invoice_business_issuers.create",
  "invoice_business_issuers.update",
] as const;

const QualifiedInvoiceIssueRequestPermissions = [
  "qualified_invoice_issue_request.list",
] as const;

const CustomerReviewPermissions = [
  "customer_reviews.list",
  "customer_reviews.download_csv",
] as const;

const ProcManagementProceduresPermissions = [
  "proc_management.procedures.create",
  "proc_management.procedures.copy",
  "proc_management.procedures.update_draft",
  "proc_management.procedures.list",
  "proc_management.procedures.search",
  "proc_management.procedures.read",
] as const;

const ProcManagementProceduresPermissionsForOrgUsers = [
  "proc_management.procedures.update_published",
  "proc_management.procedures.update_archived",
  "proc_management.procedures.update_status_from_draft_to_published",
  "proc_management.procedures.update_status_from_published_to_draft",
  "proc_management.procedures.update_status_from_draft_to_archived",
  "proc_management.procedures.update_status_from_archived_to_draft",
  "proc_management.procedures.manage_groups",
] as const;

const ProcManagementFilesPermissions = [
  "proc_management.files.upload",
] as const;

const ProcManagementPlacesPermissions = [
  "proc_management.places.create",
  "proc_management.places.update",
  "proc_management.places.list",
  "proc_management.places.read",
] as const;

const ProcManagementAreasPermissions = [
  "proc_management.areas.create",
  "proc_management.areas.update",
  "proc_management.areas.list",
  "proc_management.areas.read",
] as const;

const ProcManagementProcedureTagsPermissions = [
  "proc_management.procedure_tags.create",
  "proc_management.procedure_tags.delete",
  "proc_management.procedure_tags.update",
  "proc_management.procedure_tags.list",
] as const;

const ProcManagementConfigPermissions = [
  "proc_management.search_config.upsert",
  "proc_management.search_config.read",
] as const;

const TtzkPortalShortcutsPermissions = [
  "ttzk_portal.shortcuts.create",
  "ttzk_portal.shortcuts.update",
  "ttzk_portal.shortcuts.list",
  "ttzk_portal.shortcuts.read",
] as const;

const PortalNoticesPermissions = [
  "portal.notices.create",
  "portal.notices.update",
  "portal.notices.delete",
  "portal.notices.list",
  "portal.notices.read",
] as const;

const PortalCommonConfigPermissions = [
  "portal.common_config.read",
  "portal.common_config.update",
] as const;

export const ProcManagementAllPermissions = [
  ...ProcManagementProceduresPermissions,
  ...ProcManagementProceduresPermissionsForOrgUsers,
  ...ProcManagementFilesPermissions,
  ...ProcManagementPlacesPermissions,
  ...ProcManagementAreasPermissions,
  ...ProcManagementProcedureTagsPermissions,
  ...ProcManagementConfigPermissions,
] as const;

export const PortalAllPermissions = [
  ...TtzkPortalShortcutsPermissions,
  ...PortalNoticesPermissions,
  ...PortalCommonConfigPermissions,
] as const;

const MadoguchiEditorTemplatesPermissions = [
  "madoguchi_editor.templates.list",
] as const;

export const KintoneExportPermissions = [
  "kintone_export.setting.read",
  "kintone_export.setting.create",
  "kintone_export.setting.update",
  "kintone_export.setting.enable_disable",

  "kintone_export.exported_record.read",
  "kintone_export.exported_record.delete",

  "kintone_export.report.read",

  "kintone_export.export_request.create",
  "kintone_export.export_request.read",
] as const;

const PermissionsList = [
  // Org
  "organizations.read",
  "organizations.create",
  "organizations.update",

  // Groups
  "groups.create",
  "groups.update",
  "groups.read",

  // Procedures
  "procedures.create",
  "procedures.read",
  "procedures.update",
  "procedures.pdf_output_setting_update",
  "procedures.delete",
  "procedures.publish",
  "procedures.unpublish",
  "procedures.applications_fetch",
  "procedures.applications_csv_download",
  "procedures.presentation_read",
  "procedures.presentation_update",
  "procedures.mail_cfg_read",
  "procedures.mail_cfg_update",
  "procedures.manage_group_bulk_update",
  "procedures.count_publishing",

  // Applications
  ...ApplicationsPermissions,

  // ApplicationRemovalTasks
  ...ApplicationRemovalTasksPermissions,

  // Users
  ...UsersPermissions,

  // Service Accounts
  ...ServiceAccountsPermissions,

  // Surveys
  ...SurveysPermissions,

  // SurveyAnswers
  ...SurveyAnswersPermissions,

  // ProductDataRemovalTasks
  ...ProductDataRemovalTasksPermissions,

  // SAML settings
  ...OrgSamlSettingPermissions,

  // Session settings
  ...OrgSessionSettingPermissions,

  // Auditlogs
  "audit_logs.read",

  // PaymentRawdata
  "payments_rawdata.read",

  // QualifiedInvoiceBusinessIssuers
  ...QualifiedInvoiceBusinessIssuersPermissions,

  // QualifiedInvoiceIssueRequest
  ...QualifiedInvoiceIssueRequestPermissions,

  // Customer-Reviews
  ...CustomerReviewPermissions,

  // proc_management.procedures
  ...ProcManagementProceduresPermissions,
  ...ProcManagementProceduresPermissionsForOrgUsers,

  // proc_management.files
  ...ProcManagementFilesPermissions,

  // proc_management.places
  ...ProcManagementPlacesPermissions,

  // proc_management.areas
  ...ProcManagementAreasPermissions,

  // proc_management.procedure_tags
  ...ProcManagementProcedureTagsPermissions,

  // proc_management.config
  ...ProcManagementConfigPermissions,

  // ttzk_portal.shortcuts
  ...TtzkPortalShortcutsPermissions,

  // portal.notices
  ...PortalNoticesPermissions,

  // portal.common_config
  ...PortalCommonConfigPermissions,

  // madoguchi_editor.templates
  ...MadoguchiEditorTemplatesPermissions,

  // graffer utility tools
  "graffer_utility_tools.generate_matrix_code",

  // Customer-Reviews
  ...CustomerReviewPermissions,

  // Kintone Export
  ...KintoneExportPermissions,

  // フロントエンドでのUI表示の都合上追加している権限
  "procedures.set_pdf_output_setting",
  "procedures.manage_groups",
  "procedures.import_export_formschema",
] as const;

export type GrafferPermissions = (typeof PermissionsList)[number];
export const AllPermissions: readonly GrafferPermissions[] = PermissionsList;

export const PermissionMap: Record<string, GrafferPermissions[]> = {
  GrafferStaff: [
    ...UsersPermissions,
    ...ServiceAccountsPermissions,
    "procedures.set_pdf_output_setting",
    "procedures.create",
    "procedures.read",
    "procedures.update",
    "procedures.pdf_output_setting_update",
    "procedures.delete",
    "procedures.presentation_read",
    "procedures.presentation_update",
    "procedures.mail_cfg_read",
    "procedures.mail_cfg_update",
    "procedures.manage_groups",
    "procedures.import_export_formschema",
    "procedures.count_publishing",
    "applications.count",
    "surveys.count_publishing",
    "survey_answers.count",
    "graffer_utility_tools.generate_matrix_code",
    ...ProcManagementProceduresPermissions,
    ...ProcManagementFilesPermissions,
    ...ProcManagementPlacesPermissions,
    ...ProcManagementAreasPermissions,
    ...ProcManagementProcedureTagsPermissions,
    ...ProcManagementConfigPermissions,
    ...TtzkPortalShortcutsPermissions,
    ...PortalNoticesPermissions,
    ...PortalCommonConfigPermissions,
    ...CustomerReviewPermissions,
    ...QualifiedInvoiceBusinessIssuersPermissions,
    ...KintoneExportPermissions,
  ],
};

export const displayName = (r: Roles): string => {
  switch (r) {
    case "Admin":
      return "特権管理者";
    case "AccountManager":
      return "アカウント管理者";
    case "Staff":
      return "一般ユーザー";
    default:
      throw new Error(`Unknown role: ${r}`);
  }
};

// グループに対するRoleの表示名
// 現状は全体の権限に紐付いて表示しているが、今後はなくなる想定
export const displayGroupRole = (r: GroupRole): string => {
  switch (r) {
    case "Staff":
      return "スタッフ";
    case "GroupManager":
      return "グループ管理者";
    default:
      throw new Error(`Unknown role: ${r}`);
  }
};
